import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { isBrandAdminUiEnabled } from "../../config/brandAdminAccess";
import {
  createBrand,
  deleteBrand,
  getBrand,
  getManifestPreview,
  listBrands,
  updateBrand,
  uploadBrandAsset,
} from "../../api/brandAdmin";
import "./BrandsManagement.css";

const BRAND_ID_REGEX = /^[a-z][a-z0-9-]{1,30}$/;
const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const FEATURE_FIELDS = [
  { key: "leave", label: "الإجازات" },
  { key: "payroll", label: "الرواتب" },
  { key: "attendance", label: "الحضور والانصراف" },
  { key: "overtime", label: "الوقت الإضافي" },
  { key: "evaluations", label: "تقييم الأداء" },
  { key: "complaints", label: "الشكاوى" },
];

const ASSET_TYPES = [
  { key: "logo", label: "الشعار (logo.png)" },
  { key: "icon", label: "أيقونة التطبيق (icon.png)" },
  { key: "splash", label: "شاشة البداية (splash.png)" },
];

const EMPTY_FORM = {
  id: "",
  appName: "",
  shortName: "",
  companyName: "",
  apiUrl: "",
  colors: { primary: "#1B4F72", secondary: "#2E86C1", primaryDark: "" },
  features: FEATURE_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: true }), {}),
};

function extractErrorMessage(err, fallback) {
  const message =
    err?.response?.data?.message || err?.response?.data?.error || fallback;
  return Array.isArray(message) ? message.join("\n") : message;
}

function BrandsManagement() {
  const { user } = useAuth();
  const isAdmin = Boolean(
    user?.role === "admin" || user?.backendRoles?.includes("admin"),
  );
  const uiEnabled = isBrandAdminUiEnabled();

  const [brands, setBrands] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState(null);

  const [mode, setMode] = useState("list"); // "list" | "create" | "edit"
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);

  const [assetUploading, setAssetUploading] = useState({});
  const [assetMessage, setAssetMessage] = useState(null);

  const [previewId, setPreviewId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const loadBrands = useCallback(() => {
    setLoadingList(true);
    setListError(null);
    listBrands()
      .then((data) => setBrands(Array.isArray(data) ? data : []))
      .catch((err) =>
        setListError(extractErrorMessage(err, "تعذر تحميل قائمة البراندات")),
      )
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => {
    if (isAdmin && uiEnabled) {
      loadBrands();
    }
  }, [isAdmin, uiEnabled, loadBrands]);

  function handleStartCreate() {
    setMode("create");
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setFormSuccess(null);
    setAssetMessage(null);
  }

  async function handleStartEdit(id) {
    setMode("edit");
    setEditingId(id);
    setFormError(null);
    setFormSuccess(null);
    setAssetMessage(null);
    try {
      const brand = await getBrand(id);
      setForm({
        id: brand.id,
        appName: brand.appName || "",
        shortName: brand.shortName || "",
        companyName: brand.settings?.companyName || "",
        apiUrl: brand.apiUrl || "",
        colors: {
          primary: brand.colors?.primary || "",
          secondary: brand.colors?.secondary || "",
          primaryDark: brand.colors?.primaryDark || "",
        },
        features: { ...EMPTY_FORM.features, ...(brand.features || {}) },
      });
    } catch (err) {
      setFormError(extractErrorMessage(err, "تعذر تحميل بيانات البراند"));
    }
  }

  function handleCancel() {
    setMode("list");
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setFormSuccess(null);
  }

  function handleFieldChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleColorChange(key, value) {
    setForm((prev) => ({ ...prev, colors: { ...prev.colors, [key]: value } }));
  }

  function handleFeatureToggle(key) {
    setForm((prev) => ({
      ...prev,
      features: { ...prev.features, [key]: !prev.features[key] },
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    const trimmedId = form.id.trim();
    if (mode === "create" && !BRAND_ID_REGEX.test(trimmedId)) {
      setFormError(
        "معرّف البراند غير صالح: يجب أن يبدأ بحرف إنجليزي صغير ويحتوي فقط على أحرف صغيرة/أرقام/شرطات (2-31 حرفاً)",
      );
      return;
    }
    if (!form.appName.trim() || !form.shortName.trim()) {
      setFormError("اسم التطبيق والاسم المختصر مطلوبان");
      return;
    }
    if (
      !HEX_COLOR_REGEX.test(form.colors.primary.trim()) ||
      !HEX_COLOR_REGEX.test(form.colors.secondary.trim())
    ) {
      setFormError(
        "اللون الأساسي والثانوي يجب أن يكونا بصيغة hex صالحة (مثال: #1B4F72)",
      );
      return;
    }
    if (
      form.colors.primaryDark.trim() &&
      !HEX_COLOR_REGEX.test(form.colors.primaryDark.trim())
    ) {
      setFormError("اللون الغامق يجب أن يكون بصيغة hex صالحة أو فارغاً");
      return;
    }

    const payload = {
      appName: form.appName.trim(),
      shortName: form.shortName.trim(),
      ...(form.companyName.trim()
        ? { companyName: form.companyName.trim() }
        : {}),
      ...(form.apiUrl.trim() ? { apiUrl: form.apiUrl.trim() } : {}),
      colors: {
        primary: form.colors.primary.trim(),
        secondary: form.colors.secondary.trim(),
        ...(form.colors.primaryDark.trim()
          ? { primaryDark: form.colors.primaryDark.trim() }
          : {}),
      },
      features: { ...form.features },
    };

    setSubmitting(true);
    try {
      if (mode === "create") {
        await createBrand({ id: trimmedId, ...payload });
        setFormSuccess("تم إنشاء البراند بنجاح");
      } else {
        await updateBrand(editingId, payload);
        setFormSuccess("تم تحديث البراند بنجاح");
      }
      await loadBrands();
      setMode("list");
    } catch (err) {
      setFormError(extractErrorMessage(err, "تعذر حفظ البراند"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id) {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `هل أنت متأكد من حذف البراند "${id}"؟ لا يمكن التراجع عن هذا الإجراء.`,
      )
    ) {
      return;
    }
    try {
      await deleteBrand(id);
      if (editingId === id) handleCancel();
      await loadBrands();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(extractErrorMessage(err, "تعذر حذف البراند"));
    }
  }

  async function handleAssetUpload(assetType, file) {
    if (!file || !editingId) return;
    setAssetUploading((prev) => ({ ...prev, [assetType]: true }));
    setAssetMessage(null);
    try {
      await uploadBrandAsset(editingId, assetType, file);
      setAssetMessage({
        type: "success",
        text: `تم رفع ${ASSET_TYPES.find((a) => a.key === assetType)?.label} بنجاح`,
      });
    } catch (err) {
      setAssetMessage({
        type: "error",
        text: extractErrorMessage(err, "تعذر رفع الملف"),
      });
    } finally {
      setAssetUploading((prev) => ({ ...prev, [assetType]: false }));
    }
  }

  async function handlePreview(id) {
    setPreviewId(id);
    setPreviewData(null);
    setPreviewError(null);
    setCopySuccess(false);
    setPreviewLoading(true);
    try {
      const data = await getManifestPreview(id);
      setPreviewData(data);
    } catch (err) {
      setPreviewError(
        extractErrorMessage(err, "تعذر توليد معاينة الـ manifest"),
      );
    } finally {
      setPreviewLoading(false);
    }
  }

  function handleClosePreview() {
    setPreviewId(null);
    setPreviewData(null);
    setPreviewError(null);
  }

  async function handleCopyPreview() {
    if (!previewData) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(previewData, null, 2));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      // Clipboard API unavailable — the JSON is still visible to copy manually.
    }
  }

  if (!uiEnabled) {
    return null;
  }

  if (!isAdmin) {
    return (
      <div className="brands-admin" dir="rtl">
        <div className="brands-admin-denied">
          هذه الصفحة مخصصة لحساب أدمن النظام فقط.
        </div>
      </div>
    );
  }

  return (
    <div className="brands-admin" dir="rtl">
      <header className="brands-admin-header">
        <h1 className="brands-admin-title">إدارة براندات White-label</h1>
        <p className="brands-admin-subtitle">
          أداة داخلية للمورّد فقط — إنشاء وتعديل ملفات brands/&lt;id&gt;/brand.json
          ورفع الشعارات ومعاينة الـ manifest، بدون تشغيل أي بناء (build) من هنا.
        </p>
      </header>

      {mode === "list" && (
        <>
          <div className="brands-admin-toolbar">
            <button
              type="button"
              className="brands-admin-btn brands-admin-btn-primary"
              onClick={handleStartCreate}
            >
              + براند جديد
            </button>
            <button
              type="button"
              className="brands-admin-btn"
              onClick={loadBrands}
              disabled={loadingList}
            >
              تحديث القائمة
            </button>
          </div>

          {listError && (
            <div className="brands-admin-banner is-error">{listError}</div>
          )}
          {loadingList && (
            <div className="brands-admin-banner">جارٍ تحميل البراندات...</div>
          )}

          <div className="brands-admin-list">
            {!loadingList && brands.length === 0 && !listError && (
              <div className="brands-admin-empty">لا توجد براندات بعد</div>
            )}
            {brands.map((brand) => (
              <div
                key={brand.id}
                className={`brands-admin-card${brand.valid ? "" : " is-invalid"}`}
              >
                <div className="brands-admin-card-main">
                  <div className="brands-admin-card-id">{brand.id}</div>
                  {brand.valid ? (
                    <>
                      <div className="brands-admin-card-name">
                        {brand.companyName || brand.appName}
                      </div>
                      <div className="brands-admin-card-meta">
                        {brand.appName} · {brand.shortName}
                      </div>
                    </>
                  ) : (
                    <div className="brands-admin-card-error">
                      خطأ في brand.json: {brand.error}
                    </div>
                  )}
                </div>
                <div className="brands-admin-card-actions">
                  <button
                    type="button"
                    className="brands-admin-btn"
                    onClick={() => handlePreview(brand.id)}
                  >
                    معاينة manifest
                  </button>
                  <button
                    type="button"
                    className="brands-admin-btn"
                    onClick={() => handleStartEdit(brand.id)}
                  >
                    تعديل
                  </button>
                  <button
                    type="button"
                    className="brands-admin-btn brands-admin-btn-danger"
                    onClick={() => handleDelete(brand.id)}
                  >
                    حذف
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {(mode === "create" || mode === "edit") && (
        <div className="brands-admin-form-card">
          <div className="brands-admin-form-header">
            <h2 className="brands-admin-form-title">
              {mode === "create" ? "براند جديد" : `تعديل: ${editingId}`}
            </h2>
            <button
              type="button"
              className="brands-admin-btn"
              onClick={handleCancel}
            >
              رجوع للقائمة
            </button>
          </div>

          {formError && (
            <div className="brands-admin-banner is-error">{formError}</div>
          )}
          {formSuccess && (
            <div className="brands-admin-banner is-success">{formSuccess}</div>
          )}

          <form className="brands-admin-form" onSubmit={handleSubmit}>
            {mode === "create" && (
              <div className="brands-admin-field">
                <label className="brands-admin-label" htmlFor="brand-id">
                  المعرّف (id) — اسم مجلد brands/&lt;id&gt;
                </label>
                <input
                  id="brand-id"
                  className="brands-admin-input"
                  value={form.id}
                  onChange={(e) => handleFieldChange("id", e.target.value)}
                  placeholder="company-x"
                  required
                  disabled={submitting}
                />
              </div>
            )}

            <div className="brands-admin-field-row">
              <div className="brands-admin-field">
                <label className="brands-admin-label" htmlFor="brand-appName">
                  اسم التطبيق (appName)
                </label>
                <input
                  id="brand-appName"
                  className="brands-admin-input"
                  value={form.appName}
                  onChange={(e) =>
                    handleFieldChange("appName", e.target.value)
                  }
                  placeholder="Company X HRMS"
                  required
                  disabled={submitting}
                />
              </div>
              <div className="brands-admin-field">
                <label className="brands-admin-label" htmlFor="brand-shortName">
                  الاسم المختصر (shortName)
                </label>
                <input
                  id="brand-shortName"
                  className="brands-admin-input"
                  value={form.shortName}
                  onChange={(e) =>
                    handleFieldChange("shortName", e.target.value)
                  }
                  placeholder="CompanyX"
                  required
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="brands-admin-field-row">
              <div className="brands-admin-field">
                <label
                  className="brands-admin-label"
                  htmlFor="brand-companyName"
                >
                  اسم الشركة المعروض{" "}
                  <span className="brands-admin-optional">(اختياري)</span>
                </label>
                <input
                  id="brand-companyName"
                  className="brands-admin-input"
                  value={form.companyName}
                  onChange={(e) =>
                    handleFieldChange("companyName", e.target.value)
                  }
                  placeholder="Company X"
                  disabled={submitting}
                />
              </div>
              <div className="brands-admin-field">
                <label className="brands-admin-label" htmlFor="brand-apiUrl">
                  رابط الـ API{" "}
                  <span className="brands-admin-optional">(اختياري)</span>
                </label>
                <input
                  id="brand-apiUrl"
                  className="brands-admin-input"
                  value={form.apiUrl}
                  onChange={(e) => handleFieldChange("apiUrl", e.target.value)}
                  placeholder="https://api.example.com"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="brands-admin-field-row">
              <div className="brands-admin-field">
                <label className="brands-admin-label" htmlFor="brand-primary">
                  اللون الأساسي
                </label>
                <input
                  id="brand-primary"
                  type="text"
                  className="brands-admin-input"
                  value={form.colors.primary}
                  onChange={(e) => handleColorChange("primary", e.target.value)}
                  placeholder="#1B4F72"
                  required
                  disabled={submitting}
                />
              </div>
              <div className="brands-admin-field">
                <label className="brands-admin-label" htmlFor="brand-secondary">
                  اللون الثانوي
                </label>
                <input
                  id="brand-secondary"
                  type="text"
                  className="brands-admin-input"
                  value={form.colors.secondary}
                  onChange={(e) =>
                    handleColorChange("secondary", e.target.value)
                  }
                  placeholder="#2E86C1"
                  required
                  disabled={submitting}
                />
              </div>
              <div className="brands-admin-field">
                <label
                  className="brands-admin-label"
                  htmlFor="brand-primaryDark"
                >
                  اللون الغامق{" "}
                  <span className="brands-admin-optional">(اختياري)</span>
                </label>
                <input
                  id="brand-primaryDark"
                  type="text"
                  className="brands-admin-input"
                  value={form.colors.primaryDark}
                  onChange={(e) =>
                    handleColorChange("primaryDark", e.target.value)
                  }
                  placeholder="#0E2F44"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="brands-admin-field">
              <span className="brands-admin-label">الميزات المفعّلة</span>
              <div className="brands-admin-features">
                {FEATURE_FIELDS.map((f) => (
                  <label key={f.key} className="brands-admin-checkbox">
                    <input
                      type="checkbox"
                      checked={Boolean(form.features[f.key])}
                      onChange={() => handleFeatureToggle(f.key)}
                      disabled={submitting}
                    />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="brands-admin-form-actions">
              <button
                type="submit"
                className="brands-admin-btn brands-admin-btn-primary"
                disabled={submitting}
              >
                {submitting
                  ? "جارٍ الحفظ..."
                  : mode === "create"
                    ? "إنشاء البراند"
                    : "حفظ التعديلات"}
              </button>
            </div>
          </form>

          {mode === "edit" && (
            <div className="brands-admin-assets">
              <h3 className="brands-admin-form-title">رفع الأصول (PNG فقط)</h3>
              {assetMessage && (
                <div
                  className={`brands-admin-banner ${
                    assetMessage.type === "error" ? "is-error" : "is-success"
                  }`}
                >
                  {assetMessage.text}
                </div>
              )}
              <div className="brands-admin-field-row">
                {ASSET_TYPES.map((asset) => (
                  <div className="brands-admin-field" key={asset.key}>
                    <label className="brands-admin-label">
                      {asset.label}
                    </label>
                    <input
                      type="file"
                      accept="image/png"
                      disabled={Boolean(assetUploading[asset.key])}
                      onChange={(e) =>
                        handleAssetUpload(asset.key, e.target.files?.[0])
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {previewId && (
        <div className="brands-admin-preview-overlay">
          <div className="brands-admin-preview-panel">
            <div className="brands-admin-form-header">
              <h2 className="brands-admin-form-title">
                معاينة manifest: {previewId}
              </h2>
              <button
                type="button"
                className="brands-admin-btn"
                onClick={handleClosePreview}
              >
                إغلاق
              </button>
            </div>

            {previewLoading && (
              <div className="brands-admin-banner">جارٍ التوليد...</div>
            )}
            {previewError && (
              <div className="brands-admin-banner is-error">
                {previewError}
              </div>
            )}

            {previewData && (
              <>
                {previewData.warnings?.length > 0 && (
                  <div className="brands-admin-banner is-warning">
                    {previewData.warnings.map((w, i) => (
                      <div key={i}>{w}</div>
                    ))}
                  </div>
                )}
                <pre className="brands-admin-preview-json">
                  {JSON.stringify(previewData, null, 2)}
                </pre>
                <div className="brands-admin-form-actions">
                  <button
                    type="button"
                    className="brands-admin-btn brands-admin-btn-primary"
                    onClick={handleCopyPreview}
                  >
                    {copySuccess ? "تم النسخ ✓" : "نسخ الـ JSON"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BrandsManagement;
